import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "kt_cart";

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || { restaurant: null, items: [] };
  } catch {
    return { restaurant: null, items: [] };
  }
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(cart));
  }, [cart]);

  const addItem = useCallback((restaurant, item) => {
    setCart((prev) => {
      const sameRestaurant = prev.restaurant?.restaurant_id === restaurant.restaurant_id;
      const items = sameRestaurant ? [...prev.items] : [];
      const idx = items.findIndex((i) => i.item_id === item.item_id);
      if (idx >= 0) items[idx] = { ...items[idx], qty: items[idx].qty + 1 };
      else items.push({ item_id: item.item_id, name: item.name, price: item.price, image: item.image, veg: item.veg, qty: 1 });
      return { restaurant: { restaurant_id: restaurant.restaurant_id, name: restaurant.name, image: restaurant.image, delivery_fee: restaurant.delivery_fee, min_order: restaurant.min_order, gst_registered: !!restaurant.gst_registered, gstin: restaurant.gstin || null }, items };
    });
  }, []);

  const updateQty = useCallback((item_id, qty) => {
    setCart((prev) => {
      const items = prev.items.map((i) => (i.item_id === item_id ? { ...i, qty } : i)).filter((i) => i.qty > 0);
      return items.length ? { ...prev, items } : { restaurant: null, items: [] };
    });
  }, []);

  const clear = useCallback(() => setCart({ restaurant: null, items: [] }), []);

  const replaceCart = useCallback((restaurant, items) => {
    setCart({
      restaurant: { restaurant_id: restaurant.restaurant_id, name: restaurant.name, image: restaurant.image, delivery_fee: restaurant.delivery_fee, min_order: restaurant.min_order, pickup_enabled: restaurant.pickup_enabled, pickup_prep_min: restaurant.pickup_prep_min, gst_registered: !!restaurant.gst_registered, gstin: restaurant.gstin || null },
      items: (items || []).map((i) => ({ item_id: i.item_id, name: i.name, price: i.price, image: i.image, veg: i.veg, qty: i.qty || 1 })),
    });
  }, []);

  const subtotal = useMemo(() => cart.items.reduce((s, i) => s + i.price * i.qty, 0), [cart.items]);
  const count = useMemo(() => cart.items.reduce((s, i) => s + i.qty, 0), [cart.items]);

  const value = useMemo(() => ({ cart, addItem, updateQty, clear, replaceCart, subtotal, count }), [cart, addItem, updateQty, clear, replaceCart, subtotal, count]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
